import { NewsAPI } from "../../../src/apis/NewsAPI"
import { getNavigationProps } from "@components/Navigation/getNavigationProps"
import { SEO } from "../../../src/types/next.js"
import InferNextPropsType from "infer-next-props-type"
import { ParsedUrlQuery } from "querystring"
import { GetStaticPropsContext } from "next"
import * as NextConstants from "../../../src/constants/next"
import React from "react"
import News from "@components/News/News"
import { TeamAPI } from "../../../src/apis/TeamAPI"
import { LocalizedNews } from "../../../src/types/news"
import moment from "moment"
import { getNewsData, getSimplifiedNews, simplifySingleNews } from "../../../src/utils/news"
import { NewsListPage } from "@components/News/NewsListPage"
import { getNewsPaginationUrl } from "../../../src/utils/urls"

export default function NewsPage({
  news,
  homeTeamBettingStats,
  awayTeamBettingStats,
  isNews,
  trigger,
  newsList,
  pageNum,
  pages,
}: InferNextPropsType<typeof getStaticProps>) {
  if (isNews) {
    return (
      <News
        news={news!}
        homeTeamBettingStats={homeTeamBettingStats}
        awayTeamBettingStats={awayTeamBettingStats}
        teamStandings={[]}
        trigger={trigger}
      />
    )
  } else {
    return (
      <NewsListPage
        news={newsList!}
        pageNum={pageNum!}
        pageCount={pages!}
        onNewsPaginationChange={(page) => {
          return getNewsPaginationUrl(page)
        }}
      />
    )
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" }
}

interface IParams extends ParsedUrlQuery {
  newsCalciocomSlug: string
}

function defineSeo(news: LocalizedNews, newsCalciocomSlug: string): SEO {
  const title = news.metaTitle
    ? news.metaTitle
    : news.predictionMatch
    ? `Pronostico ${news.predictionMatch?.homeTeam?.name}-${
        news.predictionMatch?.awayTeam?.name
      } | quote del match ${moment(news.predictionMatch?.date).format("DD/MM/YYYY")}`
    : `Pronostico | quote del match`

  const description = news.metaDescription
    ? news.metaDescription
    : news.predictionMatch
    ? `Le migliori quote ed il pronostico vincente di ${news.predictionMatch?.homeTeam?.name} vs ${news.predictionMatch?.awayTeam?.name}! Scopri il risultato esatto e le probabili formazioni per il match di ${news.predictionMatch?.competition?.name}`
    : `Le migliori quote ed il pronostico vincente! Scopri il risultato esatto e le probabili formazioni per il match.`
  return {
    title: title,
    description: description,
    keywords: [`${news.title}`, "Scommesse", "Pronostici"],
    type: "article",
    image: news?.image?.path || null,
    canonicals: [`/news/${newsCalciocomSlug}`],
    url: `https://www.calcio.com/news/${newsCalciocomSlug}`,
    index: news.index,
    publishedAt: news.publishedAt,
    updatedAt: news.updatedAt,
    author: {
      name: news?.authors?.[0]?.author?.name || "Gianfranco Pastore",
      link: news?.authors?.[0]?.author?.meta.linkedin || "https://www.linkedin.com/in/gianfrancopastore/",
    },
  }
}

export async function getStaticProps(context: GetStaticPropsContext<IParams>) {
  try {
    const { newsCalciocomSlug } = context.params!

    const pageNumInt = parseInt(newsCalciocomSlug)

    if (isNaN(pageNumInt)) {
      const [news, navigationProps] = await Promise.all([
        NewsAPI.getNewsByCalciocomSlugPrediction(newsCalciocomSlug),
        getNavigationProps(),
      ])
      let trigger = null
      if (news.content.includes("triggerIframeQuote")) {
        trigger = "triggerIframeQuote"
      } else if (news.content.includes("triggerE2Quote")) {
        trigger = "triggerE2Quote"
      }

      if (news.type === "predictions") {
        const simplifiedSingleNews = simplifySingleNews(news)
        const seo: SEO = defineSeo(news, newsCalciocomSlug)
        const [homeTeamBettingStats, awayTeamBettingStats /*, teamStandings*/] = await Promise.all([
          news.predictionMatch?.homeTeam
            ? await TeamAPI.getBettingStats(news.predictionMatch.homeTeam.id, news.predictionMatch.competitionSeasonId)
            : undefined,
          news.predictionMatch?.awayTeam
            ? await TeamAPI.getBettingStats(news.predictionMatch.awayTeam.id, news.predictionMatch.competitionSeasonId)
            : undefined,
        ])

        return {
          props: {
            isNews: true,
            news: simplifiedSingleNews,
            navigationProps: navigationProps,
            title: seo.title,
            seo,
            homeTeamBettingStats,
            awayTeamBettingStats,
            trigger,
            showCenterColumn: false,
          },
          revalidate: NextConstants.REVALIDATE,
        }
      } else {
        const title = news.title
        const seo: SEO = {
          description: title,
          keywords: [`${news.title}`, "News"],
          type: "article",
          image: news?.image?.path || null,
          url: `https://www.calcio.com/news/${newsCalciocomSlug}`,
          title: news.title,
          publishedAt: news.publishedAt,
          updatedAt: news.updatedAt,
        }
        const newNews = await NewsAPI.getNewsByCalciocomSlug(newsCalciocomSlug)
        const simplifiedSingleNews = simplifySingleNews(newNews)
        const relatedNews = await NewsAPI.getRelatedNewsById(news.id)
        // reduce the content size
        relatedNews.forEach((n) => {
          n.metaDescription = null
          n.metaTitle = null
          n.content = n.content.replace(/<\/?[^>]+(>|$)/g, "").substring(0, 150) + "..."
        })
        return {
          props: {
            isNews: true,
            news: simplifiedSingleNews,
            relatedNews,
            navigationProps: navigationProps,
            title,
            seo,
            trigger,
          },
          revalidate: NextConstants.REVALIDATE,
        }
      }
    } else {
      const [props] = await Promise.all([getNewsData(pageNumInt, "generali")])

      const simplifiedNews = getSimplifiedNews(props.news)

      return {
        props: {
          pageNum: props.pageNum,
          pages: props.pages,
          newsList: simplifiedNews,
          seo: {
            ...props.seo,
            title: "Ultime notizie e risultati  |  Calcio.com",
            description:
              "Tutte le news  sul calcio: i giocatori preferiti, la tua squadra e scopri gli scoop ed i risultati in tempo reale. Tieniti aggiornato con Calcio.com",
          },
          prev: props.pageNum > 1 ? `/news/${props.pageNum - 1}` : null,
          next: props.pageNum < props.pages ? `/news/${props.pageNum + 1}` : null,
          navigationProps: props.navigationProps,
        },
        revalidate: NextConstants.REVALIDATE,
      }
    }
  } catch (error) {
    console.error(error)
    return {
      notFound: true,
    }
  }
}
